import { useState, useEffect } from 'react';
import { C, FONT } from './adminShared';

// ─── BMS pre-call form structure ──────────────────────────────────────
// Mirrors the client-facing form in frontend/src/pages/CallDay.jsx EXACTLY
// (section order, field order, and question wording). Read-only reproduction
// for the admin. `q` strings are copied verbatim from the client form so the
// modal + "Copy all" output read like the client's own filled-in form.
const SECTIONS = [
  {
    title: 'Your Week',
    fields: [
      { key: 'week_landing',        type: 'array', q: 'How did the week land overall?' },
      { key: 'biggest_realisation', type: 'text',  q: 'What was the single biggest realisation or shift for you this week?' },
    ],
  },
  {
    title: 'Your Three Pillars',
    note: 'Rate how eroded each pillar feels for you right now. 0 = completely eroded / 5 = feeling strong',
    fields: [
      { key: 'inner_ownership_score',      type: 'score', q: 'Inner Ownership — how much you trust your own perspective, opinions, values and beliefs' },
      { key: 'emotional_legitimacy_score', type: 'score', q: 'Emotional Legitimacy — how safe you feel to feel and express your emotions without judging or overriding them' },
      { key: 'aligned_action_score',       type: 'score', q: 'Aligned Action — how often your actions actually reflect what feels true for you, rather than what feels safe' },
    ],
  },
  {
    title: 'Your Loops & Beliefs',
    fields: [
      { key: 'main_loop',        type: 'text',    q: "Describe the loop, belief, emotion, trigger or action you most want to work on today. Just give me a picture of the pattern — it doesn't need to be perfectly mapped out." },
      { key: '__beliefs',        type: 'beliefs', q: 'What are the top 1–3 beliefs you feel are most keeping you stuck right now?' },
      { key: 'pattern_duration', type: 'text',    q: 'How long have these patterns been running?' },
    ],
  },
  {
    title: 'Where You Want to Go',
    fields: [
      { key: 'successful_session', type: 'text',  q: 'What would a successful Breakthrough Mapping Session look like for you today? What do you want to walk away feeling, knowing or having seen more clearly?' },
      { key: 'life_area_impacted', type: 'array', q: 'What area of your life feels most impacted by your conditioning right now?' },
      { key: 'support_score',      type: 'score', q: 'How supported do you feel in this work by the people around you right now?' },
      { key: 'readiness_score',    type: 'score', q: 'How ready do you feel to go deeper into this work beyond the course?' },
      { key: 'anything_else',      type: 'text',  q: 'Is there anything else you want me to know before our session today?' },
    ],
  },
];

const NOT_ANSWERED = '(not answered)';

// Resolve a single field's answer to display/copy text. Empty string, null,
// and empty array all render as "(not answered)".
const answerText = (form, field) => {
  const v = form?.[field.key];
  if (field.type === 'score') return (v === null || v === undefined) ? NOT_ANSWERED : `${v} / 5`;
  if (field.type === 'array') return (Array.isArray(v) && v.length) ? v.join(', ') : NOT_ANSWERED;
  return (typeof v === 'string' && v.trim()) ? v : NOT_ANSWERED;
};

// The three belief slots as [label, answer] pairs.
const beliefRows = (form) =>
  [1, 2, 3].map(n => [`Belief ${n}`, (typeof form?.[`belief_${n}`] === 'string' && form[`belief_${n}`].trim()) ? form[`belief_${n}`] : NOT_ANSWERED]);

const fmtSubmitted = (iso) =>
  iso ? new Date(iso).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

// Build the plain-text "Copy all" payload: self-contained header + every
// question on its own line as "Question: answer", in display order.
const buildCopyText = (contact) => {
  const form = contact.bmsForm || {};
  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || '(no name)';
  const lines = [
    `BMS Pre-Call Form — ${name} (${contact.email || 'no email'})`,
    `Submitted: ${fmtSubmitted(form.submitted_at)}`,
  ];
  for (const section of SECTIONS) {
    lines.push('', section.title);
    for (const field of section.fields) {
      if (field.type === 'beliefs') {
        lines.push(field.q);
        for (const [label, ans] of beliefRows(form)) lines.push(`${label}: ${ans}`);
      } else {
        lines.push(`${field.q}: ${answerText(form, field)}`);
      }
    }
  }
  return lines.join('\n');
};

// ─── Modal ────────────────────────────────────────────────────────────
export const BmsFormModal = ({ contact, onClose }) => {
  const form = contact.bmsForm || {};
  const [copied, setCopied]   = useState(false);
  const [copyErr, setCopyErr] = useState(false);

  // Esc closes (nice-to-have alongside the backdrop click).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildCopyText(contact));
      setCopyErr(false);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
      setCopyErr(true);
    }
  };

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');

  const CopyButton = () => (
    <button
      onClick={doCopy}
      style={{
        padding: '7px 14px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT,
        border: `1px solid ${C.heading}`, background: copied ? C.green : C.heading,
        color: '#FFF8F7', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
      }}
    >
      {copied ? '✓ Copied' : 'Copy all'}
    </button>
  );

  const QuestionBlock = ({ q, children }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, color: C.accent, fontStyle: 'italic', lineHeight: 1.55, marginBottom: 6 }}>{q}</div>
      {children}
    </div>
  );

  const AnswerText = ({ text }) => {
    const empty = text === NOT_ANSWERED;
    return (
      <div style={{ fontSize: 13, color: empty ? C.accent : C.text, fontStyle: empty ? 'italic' : 'normal', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
        {text}
      </div>
    );
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto' }}
    >
      <div style={{ background: C.card, borderRadius: 14, maxWidth: 660, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', border: `1px solid ${C.border}`, fontFamily: FONT, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header (sticky-ish, top Copy all) */}
        <div style={{ padding: '20px 24px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontFamily: "'Forum', serif", fontSize: '1.2rem', color: C.heading, letterSpacing: '0.03em' }}>
                BMS Pre-Call Form
              </h3>
              <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>
                {name}{name && contact.email ? ' · ' : ''}<span style={{ fontWeight: 400, color: C.accent }}>{contact.email}</span>
              </div>
              <div style={{ fontSize: 11, color: C.accent, marginTop: 3 }}>
                Submitted {fmtSubmitted(form.submitted_at)}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <CopyButton />
              <button
                onClick={onClose}
                aria-label="Close"
                style={{ padding: '6px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: FONT, border: `1px solid ${C.border}`, background: C.card, color: C.accent, fontSize: 14, fontWeight: 700, lineHeight: 1 }}
              >
                ✕
              </button>
            </div>
          </div>
          {copyErr && (
            <div style={{ marginTop: 8, fontSize: 11, color: C.red }}>
              Couldn't copy to clipboard — select the text manually.
            </div>
          )}
        </div>

        {/* Body — scrollable */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1 }}>
          {SECTIONS.map((section, si) => (
            <div key={section.title} style={{ paddingBottom: 20, marginBottom: 20, borderBottom: si < SECTIONS.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.heading, marginBottom: section.note ? 4 : 14 }}>
                {si + 1}. {section.title}
              </div>
              {section.note && (
                <div style={{ fontSize: 11, color: C.accent, fontStyle: 'italic', marginBottom: 14, lineHeight: 1.5 }}>{section.note}</div>
              )}
              {section.fields.map(field => {
                if (field.type === 'beliefs') {
                  return (
                    <QuestionBlock key={field.key} q={field.q}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {beliefRows(form).map(([label, ans]) => {
                          const empty = ans === NOT_ANSWERED;
                          return (
                            <div key={label} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em', minWidth: 54 }}>{label}</span>
                              <span style={{ fontSize: 13, color: empty ? C.accent : C.text, fontStyle: empty ? 'italic' : 'normal', lineHeight: 1.6 }}>{ans}</span>
                            </div>
                          );
                        })}
                      </div>
                    </QuestionBlock>
                  );
                }
                if (field.type === 'score') {
                  const text = answerText(form, field);
                  const empty = text === NOT_ANSWERED;
                  return (
                    <QuestionBlock key={field.key} q={field.q}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: empty ? C.accent : C.heading, fontStyle: empty ? 'italic' : 'normal' }}>
                        {empty ? NOT_ANSWERED : text}
                      </div>
                    </QuestionBlock>
                  );
                }
                return (
                  <QuestionBlock key={field.key} q={field.q}>
                    <AnswerText text={answerText(form, field)} />
                  </QuestionBlock>
                );
              })}
            </div>
          ))}

          {/* Bottom Copy all */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 4 }}>
            <CopyButton />
          </div>
        </div>
      </div>
    </div>
  );
};
